import { useState, useRef, useEffect } from "react";
import AdminLayout from "../components/AdminLayout";
import { useTemplate, SITE_TEMPLATES, PRESET_COLORS } from "@/context/TemplateContext";
import { request } from "@/utils/api";

// 导入模板配置（从 TemplateContext）
const siteTemplates = SITE_TEMPLATES;

const loginStyles = [
  { id: "ls1", name: "居中卡片", desc: "白色卡片居中，简洁大方", preview: "https://readdy.ai/api/search-image?query=centered%20white%20card%20login%20page%20design%20clean%20minimalist%20light%20background%20form%20centered%20modern%20UI&width=240&height=160&seq=ls1&orientation=landscape" },
  { id: "ls2", name: "左图右表单", desc: "左侧大图，右侧登录表单", preview: "https://readdy.ai/api/search-image?query=split%20screen%20login%20page%20left%20image%20right%20form%20modern%20clean%20design%20professional%20business%20website&width=240&height=160&seq=ls2&orientation=landscape" },
  { id: "ls3", name: "全屏背景图", desc: "全屏背景图片，卧式卡片", preview: "https://readdy.ai/api/search-image?query=fullscreen%20background%20image%20login%20page%20overlay%20card%20form%20modern%20elegant%20design%20business%20website&width=240&height=160&seq=ls3&orientation=landscape" },
  { id: "ls4", name: "渐变色背景", desc: "渐变色背景，现代感十足", preview: "https://readdy.ai/api/search-image?query=gradient%20background%20login%20page%20modern%20colorful%20gradient%20white%20card%20form%20clean%20UI%20design&width=240&height=160&seq=ls4&orientation=landscape" },
  { id: "ls5", name: "深色模式", desc: "深色背景，高端大气", preview: "https://readdy.ai/api/search-image?query=dark%20mode%20login%20page%20dark%20background%20white%20card%20form%20elegant%20modern%20professional%20design&width=240&height=160&seq=ls5&orientation=landscape" },
  { id: "ls6", name: "几何图案", desc: "几何图案装饰，艺术感强", preview: "https://readdy.ai/api/search-image?query=geometric%20pattern%20login%20page%20abstract%20shapes%20background%20modern%20artistic%20clean%20form%20design&width=240&height=160&seq=ls6&orientation=landscape" },
  { id: "ls7", name: "插图风格", desc: "可爱插图装饰，清新活泼", preview: "https://readdy.ai/api/search-image?query=illustration%20style%20login%20page%20cute%20flat%20illustration%20decoration%20clean%20white%20form%20modern%20friendly%20design&width=240&height=160&seq=ls7&orientation=landscape" },
  { id: "ls8", name: "极简线条", desc: "极简线条设计，优雅精致", preview: "https://readdy.ai/api/search-image?query=ultra%20minimal%20line%20art%20login%20page%20white%20background%20thin%20lines%20elegant%20minimalist%20clean%20form%20design&width=240&height=160&seq=ls8&orientation=landscape" },
  { id: "ls9", name: "卡通小清新", desc: "卡通风格，轻松活泼", preview: "https://readdy.ai/api/search-image?query=cartoon%20style%20login%20page%20playful%20colorful%20friendly%20clean%20white%20form%20modern%20fun%20design&width=240&height=160&seq=ls9&orientation=landscape" },
  { id: "ls10", name: "商务专业风", desc: "商务风格，专业可信", preview: "https://readdy.ai/api/search-image?query=corporate%20professional%20business%20login%20page%20formal%20clean%20white%20blue%20accent%20trustworthy%20modern%20design&width=240&height=160&seq=ls10&orientation=landscape" },
];

// 预设颜色（从 TemplateContext 导入）
const presetColors = PRESET_COLORS;

function SaveBar({ onSave, saved, saving, error }: { onSave: () => void; saved: boolean; saving?: boolean; error?: string }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <button
        onClick={onSave}
        disabled={saving}
        className="px-6 py-2.5 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {saving ? (
          <>
            <i className="ri-loader-2-line animate-spin" />
            保存中...
          </>
        ) : (
          "保存设置"
        )}
      </button>
      {saved && (
        <span className="text-sm text-green-600 flex items-center gap-1.5">
          <i className="ri-checkbox-circle-line" />
          保存成功
        </span>
      )}
      {error && (
        <span className="text-sm text-red-600 flex items-center gap-1.5">
          <i className="ri-error-warning-line" />
          {error}
        </span>
      )}
    </div>
  );
}

type Section = "template" | "login" | "color";

const sectionTabs: { key: Section; label: string; icon: string }[] = [
  { key: "template", label: "模版选择", icon: "ri-layout-4-line" },
  { key: "login", label: "登录注册风格", icon: "ri-login-circle-line" },
  { key: "color", label: "颜色配置", icon: "ri-palette-line" },
];

export default function AdminDecorationPage() {
  // 使用 TemplateContext
  const { 
    templateId, 
    setTemplateId, 
    loginStyle, 
    setLoginStyle, 
    themeColor, 
    setThemeColor,
    saveToServer 
  } = useTemplate();

  const [activeSection, setActiveSection] = useState<Section>("template");
  const [selectedLoginStyle, setSelectedLoginStyle] = useState(loginStyle);
  const [selectedColor, setSelectedColor] = useState(themeColor);
  const [customColor, setCustomColor] = useState(themeColor);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const colorInputRef = useRef<HTMLInputElement>(null);

  // 同步 context 状态
  useEffect(() => {
    setSelectedLoginStyle(loginStyle);
  }, [loginStyle]);

  useEffect(() => {
    setSelectedColor(themeColor);
    setCustomColor(themeColor);
  }, [themeColor]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setErrorMsg('');
    try {
      // 保存到 localStorage
      setTemplateId(templateId);
      setLoginStyle(selectedLoginStyle);
      setThemeColor(selectedColor);

      // 保存到服务器（错误会抛出，不会被吞掉）
      await saveToServer();

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      const msg = error instanceof Error ? error.message : '保存失败';
      console.error('保存失败:', msg);
      setErrorMsg(msg);
    } finally {
      setSaving(false);
    }
  };

  const handlePresetColor = (value: string) => {
    setSelectedColor(value);
    setCustomColor(value);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomColor(e.target.value);
    setSelectedColor(e.target.value);
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div>
          <h1 className="text-xl font-bold text-gray-800">网站装修</h1>
          <p className="text-sm text-gray-500 mt-1">自定义网站整体风格、模版、颜色和登录注册页样式</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
          {/* Section switcher */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
            {sectionTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveSection(tab.key)}
                className={`px-5 py-2 text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeSection === tab.key
                    ? "bg-white text-gray-800 font-medium"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <i className={`${tab.icon} text-sm`} />
                  {tab.label}
                </span>
              </button>
            ))}
          </div>

          {/* ── 模版选择 ── */}
          {activeSection === "template" && (
            <div className="space-y-5">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">网站模版</h3>
                <p className="text-xs text-gray-400 mt-0.5">选择网站整体风格和主题色调，选中后点击保存生效</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {siteTemplates.map((tpl) => (
                  <button
                    key={tpl.id}
                    onClick={() => setTemplateId(tpl.id)}
                    className={`relative rounded-2xl overflow-hidden border-2 transition-all cursor-pointer text-left w-full ${
                      templateId === tpl.id
                        ? "border-orange-400"
                        : "border-gray-100 hover:border-gray-200"
                    }`}
                  >
                    {/* 选中角标 */}
                    {templateId === tpl.id && (
                      <div className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center rounded-full bg-orange-500">
                        <i className="ri-check-line text-white text-sm" />
                      </div>
                    )}
                    {/* 模版预览图 — 竖向完整展示 */}
                    <div className="w-full overflow-hidden bg-gray-50" style={{ height: 320 }}>
                      <img
                        src={tpl.preview}
                        alt={tpl.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    {/* 底部信息栏 */}
                    <div className="px-4 py-3 flex items-center gap-3" style={{ background: tpl.bg }}>
                      <div className="w-4 h-4 rounded-full shrink-0" style={{ background: tpl.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-800">{tpl.name}</div>
                        <div className="text-xs text-gray-400">{tpl.desc}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <SaveBar onSave={handleSave} saved={saved} saving={saving} error={errorMsg} />
            </div>
          )}

          {/* ── 登录注册风格 ── */}
          {activeSection === "login" && (
            <div className="space-y-5">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">登录注册页风格</h3>
                <p className="text-xs text-gray-400 mt-0.5">选择登录和注册页面的展示风格，共 10 套可选</p>
              </div>
              <div className="grid grid-cols-2 gap-5">
                {loginStyles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => setSelectedLoginStyle(style.id)}
                    className={`relative rounded-2xl overflow-hidden border-2 transition-all cursor-pointer text-left ${
                      selectedLoginStyle === style.id
                        ? "border-orange-400"
                        : "border-gray-100 hover:border-gray-200"
                    }`}
                  >
                    <div className="w-full h-32 overflow-hidden">
                      <img
                        src={style.preview}
                        alt={style.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="p-3 flex items-center justify-between bg-white">
                      <div>
                        <div className="text-sm font-semibold text-gray-800">{style.name}</div>
                        <div className="text-xs text-gray-400">{style.desc}</div>
                      </div>
                      {selectedLoginStyle === style.id && (
                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-orange-500 shrink-0">
                          <i className="ri-check-line text-white text-xs" />
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <SaveBar onSave={handleSave} saved={saved} saving={saving} error={errorMsg} />
            </div>
          )}

          {/* ── 颜色配置 ── */}
          {activeSection === "color" && (
            <div className="space-y-7">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">网站主题颜色</h3>
                <p className="text-xs text-gray-400 mt-0.5">选择网站主色调，影响按钮、链接、高亮等全局颜色</p>
              </div>

              {/* 当前颜色预览 */}
              <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
                <div
                  className="w-14 h-14 rounded-xl shrink-0 transition-colors duration-300"
                  style={{ background: selectedColor }}
                />
                <div>
                  <div className="text-sm font-semibold text-gray-800">当前主题色</div>
                  <div className="text-xs text-gray-400 mt-0.5 font-mono">{selectedColor.toUpperCase()}</div>
                  <div className="mt-2 flex gap-2">
                    <span
                      className="inline-block px-3 py-1 text-xs text-white rounded-full font-medium"
                      style={{ background: selectedColor }}
                    >
                      按钮示例
                    </span>
                    <span
                      className="inline-block px-3 py-1 text-xs rounded-full font-medium border"
                      style={{ color: selectedColor, borderColor: selectedColor }}
                    >
                      边框示例
                    </span>
                  </div>
                </div>
              </div>

              {/* 预设颜色 */}
              <div className="space-y-3">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">快速选择（10种预设）</div>
                <div className="grid grid-cols-5 gap-3">
                  {presetColors.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => handlePresetColor(c.value)}
                      title={c.name}
                      className={`group flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                        selectedColor === c.value
                          ? "border-gray-400 bg-gray-50"
                          : "border-transparent hover:border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="relative w-10 h-10 flex items-center justify-center">
                        <div
                          className="w-10 h-10 rounded-full transition-transform group-hover:scale-105"
                          style={{ background: c.value }}
                        />
                        {selectedColor === c.value && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <i className="ri-check-line text-white text-sm font-bold" />
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">{c.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 自定义颜色 */}
              <div className="space-y-3">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">自定义颜色</div>
                <div className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50">
                  <div
                    className="relative w-12 h-12 rounded-xl overflow-hidden cursor-pointer shrink-0 border-2 border-white"
                    style={{ background: customColor }}
                    onClick={() => colorInputRef.current?.click()}
                  >
                    <input
                      ref={colorInputRef}
                      type="color"
                      value={customColor}
                      onChange={handleCustomColorChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className="ri-edit-line text-white text-sm" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.4)" }} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-700">点击色块打开颜色选择器</div>
                    <div className="text-xs text-gray-400 mt-0.5">或直接输入十六进制颜色值</div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-gray-500 font-mono">#</span>
                      <input
                        type="text"
                        value={customColor.replace("#", "")}
                        onChange={(e) => {
                          const val = "#" + e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
                          setCustomColor(val);
                          if (val.length === 7) setSelectedColor(val);
                        }}
                        maxLength={6}
                        placeholder="F97316"
                        className="w-28 px-2 py-1 text-sm font-mono border border-gray-200 rounded-md bg-white focus:outline-none focus:border-gray-400"
                      />
                      <button
                        onClick={() => handlePresetColor(customColor)}
                        className="px-3 py-1 text-xs bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        应用
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <SaveBar onSave={handleSave} saved={saved} saving={saving} error={errorMsg} />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
